import { useMemo, useState } from 'react';
import './accounting.css';
import { useNotify } from '../../components/notifications';
import { useDeleteFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { DataTable, type DTColumn } from './DataTable';
import { useFetchMs } from '../../hooks/useFetchMs';
import { useNavActions } from '../../hooks/useNavActions';
import { SettingsDetailPage, type DetailDataAdapter, type DetailConfig } from '../modules/SettingsDetailPage';
import type { CrudField } from '../modules/moduleCrud';
import {
  useRegions, useSaveRegion, useDeleteRegion, errMessage, type Region,
} from './accounting';

interface Props { onHome?: () => void; }

/** ISO date -> MM/dd/yyyy (blank if empty/invalid). */
const fmtDate = (s: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getMonth() + 1)}/${p(d.getDate())}/${d.getFullYear()}`;
};

const COLUMNS: DTColumn<Region>[] = [
  { key: 'regionName',      label: 'RegionName',      value: (r) => r.regionName },
  { key: 'createdDate',     label: 'CreatedDate',     value: (r) => fmtDate(r.createdDate) },
  { key: 'createdBy',       label: 'CreatedBy',       value: (r) => r.createdBy ?? '' },
  { key: 'lastUpdatedDate', label: 'LastUpdatedDate', value: (r) => fmtDate(r.lastUpdatedDate) },
  { key: 'lastUpdatedBy',   label: 'LastUpdatedBy',   value: (r) => r.lastUpdatedBy ?? '' },
];

/** Region detail fields — drives the shared <SettingsDetailPage>. */
const REGION_FIELDS: CrudField[] = [
  { key: 'regionName',  label: 'Region Name', type: 'text', required: true,
    requiredMessage: 'Region Name must not be empty.' },
  { key: 'description', label: 'Description',  type: 'textarea' },
];
const REGION_CONFIG: DetailConfig = {
  fields: REGION_FIELDS, formName: 'Region', entityLabel: 'Region', idField: 'id',
  // Detail-view field layout standard (standing rule 12, 2026-09-23): label
  // BESIDE its field in a fixed-width left-aligned label column, uniform 26px
  // controls. Migrated as its own reviewed change (owner request 2026-09-24) —
  // the rule requires that rather than a silent side-effect.
  //
  // ONE column, not the 50/50 split: with only two fields a second column leaves
  // half the row empty and splits a pair that reads as one group (owner,
  // 2026-09-24). The 50/50 split is for forms with enough fields to fill it.
  singleColumn: true,
  sideLabels: true,
};

/** Settings ▸ Utility ▸ Accounting ▸ Region — editable master list.
 *  New / Edit opens the shared full-page <SettingsDetailPage> (BO UI Standard §4,
 *  standing rule 12) via a data adapter over the existing Region mutations —
 *  no bespoke popup. */
export function RegionPage({ onHome }: Props) {
  const notify = useNotify();
  // Module Access Control: hide (never grey) the actions the role does not hold.
  const perm = useNavActions('acc-region');
  const runDelete = useDeleteFlow();
  const { data: rows = [], isFetching, refetch } = useRegions();
  const loadMs = useFetchMs(isFetching);
  const save = useSaveRegion();
  const del = useDeleteRegion();

  // undefined = list view; null = New detail; a Region = Edit detail.
  const [editing, setEditing] = useState<Region | null | undefined>(undefined);
  const openNew = () => setEditing(null);
  const openEdit = (r: Region) => setEditing(r);
  const close = () => setEditing(undefined);

  // Shared ALPACA-style delete flow over the CHECKED rows (toolbar Delete): ONE
  // centred danger confirm → delete each → "Deleted." (owner request 2026-10-13 —
  // Delete moved from the per-row Actions column to the toolbar on selection).
  const removeSelected = async (sel: Region[]) => {
    if (sel.length === 0) return;
    const one = sel.length === 1 ? sel[0] : undefined;
    await runDelete({
      formName: 'Region',
      message: one ? `Delete region "${one.regionName}"?` : `Delete ${sel.length} regions?`,
      delete: async () => { for (const r of sel) await del.mutateAsync(r.id); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  const siblingIds = useMemo(() => rows.map((r) => r.id), [rows]);

  // Adapter: plug the existing Region mutations + list into the shared detail
  // view.  Memoised on `rows` so the detail's load effect stays stable.
  const adapter = useMemo<DetailDataAdapter>(() => ({
    load: async (rid) => {
      const r = rows.find((x) => x.id === rid);
      return { regionName: r?.regionName ?? '', description: r?.description ?? '' };
    },
    create: async (payload) => {
      await save.mutateAsync({
        regionName: String(payload.regionName ?? '').trim(),
        description: payload.description == null ? null : String(payload.description),
      });
      return null; // server id not surfaced by the mutation; Save→list
    },
    update: async (rid, payload) => {
      await save.mutateAsync({
        id: rid,
        regionName: String(payload.regionName ?? '').trim(),
        description: payload.description == null ? null : String(payload.description),
      });
    },
    // save.mutateAsync is stable across renders; rows is the only real dep.
  }), [rows]); // eslint-disable-line react-hooks/exhaustive-deps

  if (editing !== undefined) {
    return (
      <SettingsDetailPage
        config={REGION_CONFIG}
        adapter={adapter}
        id={editing ? editing.id : null}
        title="Region"
        group="Accounting"
        siblingIds={siblingIds}
        onHome={onHome ?? (() => {})}
        onNavigate={(nid) => { const r = rows.find((x) => x.id === nid); if (r) openEdit(r); }}
        onClose={close}
        // Utility ▸ Accounting form rendering SettingsDetailPage directly (not via
        // ModuleListView), so de-yellowed + square-cornered here like every other
        // Utility detail (owner request 2026-10-13).
        forceSquareControls
        onSaved={() => { /* useSaveRegion invalidates the list query automatically */ }}
      />
    );
  }

  return (
    <div className="bl-page mlv-host">
      <div className="bl-top">
        <div className="dp-breadcrumb" style={{ paddingTop: 12 }}>
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" />
          <span>Utility</span><i className="ti ti-chevron-right" />
          <span>Accounting</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Region</span>
        </div>
        {/* BO UI Standard v2: shared FormTitle heading (nav icon tile + title +
            the SINGLE top load-time chip) ON the grey page, then "N items". */}
        <div className="dp-page-hdr mlv-hdr--empty">
          <div className="dp-title-row">
            <FormTitle pageId="acc-region" loadMs={loadMs} loading={isFetching} />
          </div>
        </div>
      </div>

      {/* Toolbar row · search row · ONE white grid card (composed by DataTable). */}
      <DataTable exportName="Region"
        columns={COLUMNS} rows={rows} getId={(r) => r.id} loading={isFetching}
        emptyText="No regions yet — click New to add one."
        onNew={perm.create ? openNew : undefined}
        onEdit={perm.write ? openEdit : undefined}
        onDeleteSelected={perm.delete ? removeSelected : undefined}
        onRefresh={() => void refetch()} />
    </div>
  );
}
